// Size-sensitive module: it sits in the splitChunks cluster documented under CLUSTER NOTE in
// config/webpack/splitChunksBudget.ts. Read that before growing this file, comments included.
import { mergeQueryParamsToUrl } from '@shared/utils/urlHelpers';
import { useCallback, useMemo } from 'react';
import { useQueryParamHelper } from '@shared/core/queryString';
import { usePrioritizeTagParam } from '@shared/hooks/usePrioritizeTagParam';
import { PRIORITIZE_TAG_PARAM, TRANSFERRED_STAYS_QUERY_PARAMS } from '@shared/constants/accommodations';

interface UseRetainSupportedStaysParams {
  retainSupportedStaysParams: (targetUrl: string) => string;
}

/**
 * A custom hook that retains supported query parameters (e.g., `discountCode`)
 * from the current URL and merges them into any target URL.
 *
 * @returns {UseRetainSupportedStaysParams} An object containing a function to enhance URLs with retained query parameters.
 */
export const useRetainSupportedStaysParams = (): UseRetainSupportedStaysParams => {
  const queryParamHelper = useQueryParamHelper();
  const discountCode = queryParamHelper.getValueString(TRANSFERRED_STAYS_QUERY_PARAMS.discountCode);
  const joyReferralRewardCode = queryParamHelper.getValueString(TRANSFERRED_STAYS_QUERY_PARAMS.joyReferralRewardCode);
  const joyReferralCode = queryParamHelper.getValueString(TRANSFERRED_STAYS_QUERY_PARAMS.joyReferralCode);
  const eventGroupCode = queryParamHelper.getValueString(TRANSFERRED_STAYS_QUERY_PARAMS.weddingCode);
  // The curated-tag hint (`honeymoon`) that makes a search a honeymoon search. Retained so the hotel
  // page's "modify search" link, and every other hop back into search, keeps the tag.
  const prioritizeTag = usePrioritizeTagParam();

  // Stable across renders on purpose: callers hold this in `useMemo`/`useCallback` dependency lists
  // (`hotelRoutes.ts` puts it in both `buildUrlWithPreservedUtm` and the route-map memo below it),
  // and a fresh function every render had been quietly defeating those.
  //
  // Each param is spread in only when the current URL carries it. `mergeQueryParamsToUrl` lays these
  // over the target's own query, so an `undefined` here would delete a value the caller put on the
  // link — the same bug `useRetainSupportedParamsInStaysRedirectUrl` documents.
  const retainSupportedStaysParams = useCallback(
    (targetUrl: string): string => {
      // The tag is also the one param callers set on the link themselves (the honeymoon CTAs,
      // tag-configured placements), so a tag the link already names beats the current page's.
      const targetHasTag = new URLSearchParams(targetUrl.split('#')[0].split('?')[1] ?? '').has(PRIORITIZE_TAG_PARAM);
      return mergeQueryParamsToUrl(targetUrl, {
        ...(discountCode && { discountCode }),
        ...(joyReferralRewardCode && { joyReferralRewardCode }),
        ...(joyReferralCode && { joyReferralCode }),
        ...(eventGroupCode && { weddingCode: eventGroupCode }),
        ...(prioritizeTag && !targetHasTag && { prioritizeTag }),
      });
    },
    [discountCode, joyReferralRewardCode, joyReferralCode, eventGroupCode, prioritizeTag],
  );

  return useMemo(() => ({ retainSupportedStaysParams }), [retainSupportedStaysParams]);
};
